import * as React from "react";
import { useMemo, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate, useSearchParams } from "react-router-dom";
import UserService from "../services/UserService";

const theme = createTheme();

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = useMemo(() => searchParams.get("email") || "", [searchParams]);
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const linkInvalid = !email || !token;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      setErrors(["Passwords do not match."]);
      return;
    }
    if (password.length < 6) {
      setErrors(["Password must be at least 6 characters."]);
      return;
    }
    setErrors([]);
    setIsSubmitting(true);
    const result = await UserService.resetPassword(email, token, password);
    setIsSubmitting(false);
    if (result.errors && result.errors.length > 0) {
      setErrors(result.errors);
      return;
    }
    setSuccess(true);
    setTimeout(() => navigate("/login"), 2000);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box sx={{ marginTop: 8, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Reset Password
          </Typography>
          {linkInvalid ? (
            <Box sx={{ mt: 3, minWidth: 250 }}>
              <Alert severity="error">This password reset link is invalid. Please request a new one.</Alert>
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Link href="/forgot-password" variant="body2">
                  Request a new link
                </Link>
              </Box>
            </Box>
          ) : success ? (
            <Box sx={{ mt: 3, minWidth: 250 }}>
              <Alert severity="success">Password updated. Redirecting to sign in...</Alert>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, minWidth: 250 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="New Password"
                type="password"
                id="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="confirmPassword"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {errors.map((e) => (
                <div key={e} className="error-message">
                  {e}
                </div>
              ))}
              <Button type="submit" fullWidth variant="contained" disabled={isSubmitting} sx={{ mt: 3, mb: 2 }}>
                Reset Password
              </Button>
              <Box sx={{ textAlign: "center" }}>
                <Link href="/login" variant="body2">
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
